import shoppingListTemplate from "../templates/pages/shoppinglist.html?raw";
import NavBar from "../sections/navbar.ts";
import { Page } from "./base.ts";
import { getElementById, getElementDataForEach, getElementDataModel, setContent, setError, setLoading } from "../services/dom.ts";
import Footer from "../sections/footer.ts";

export default class ShoppingListPage extends Page {
  private startDate: Date = new Date();
  private endDate: Date = new Date();
  private extraIngredientOptions: { ingredient_name?: string, quantity?: number, unit_suffix?: string } = {};
  private currentMonth: Date = new Date();
  private hoverDate: Date | null = null;
  private isSelectingRange: boolean = false;

  constructor() {
    super(shoppingListTemplate);
    this.loadPage();
  }

  private async loadPage(): Promise<void> {
    // Create navbar and footer immediately
    new NavBar();
    new Footer();

    // Setup UI
    this.setupExtraItemsForm();
    this.setupListeners();
    super.handleCustomAttributes();
    this.handleDataModels();

    // Initialize stores and load data
    await super.init();
    await this.loadData();

    setContent("shoppingList");
  }

  private setupListeners(): void {
    // Initialize calendar with default values
    this.initializeDateInputs();
    this.setupCalendar();

    getElementById("extraItemButton").addEventListener("click", async () => {
      await this.addExtraItem();
    });
    getElementById("clearAllButton").addEventListener("click", async () => {
      await this.clearAllItems();
    });
  }

  private handleDataModels(): void {
    // Handle Data Models (input with value that is kept in state as it changes)
    getElementDataModel("inputExtraIngredient").addEventListener("input", () => {
      const value = (getElementDataModel("inputExtraIngredient") as HTMLInputElement).value;
      this.extraIngredientOptions.ingredient_name = value;
    });

    getElementDataModel("inputExtraUnit").addEventListener("change", () => {
      const value = (getElementDataModel("inputExtraUnit") as HTMLSelectElement).value;
      this.extraIngredientOptions.unit_suffix = value;
    });

    getElementDataModel("inputExtraQuantity").addEventListener("input", () => {
      const value = (getElementDataModel("inputExtraQuantity") as HTMLInputElement).value;
      this.extraIngredientOptions.quantity = parseInt(value);
    });
  }

  private async loadData(): Promise<void> {
    await this.getShoppingList();
    this.populateList();
  }

  private setupExtraItemsForm(): void {
    const newIngredientTemplate = document.querySelector("[data-for-each=newIngredients]") as HTMLElement;
    this.ingredientStore.ingredients.forEach((ingredient: any) => {
      const ingredientOptionElement = newIngredientTemplate.cloneNode(true);
      ingredientOptionElement.textContent = ingredient.name;
      newIngredientTemplate.parentElement!.appendChild(ingredientOptionElement);
    });
    const newIngredientUnitTemplate = document.querySelector("[data-for-each=newIngredientUnits]") as HTMLElement;
    this.unitStore.units.forEach((unit: any) => {
      const unitOptionElement = newIngredientUnitTemplate.cloneNode(true) as HTMLOptionElement;
      unitOptionElement.textContent = unit.name;
      unitOptionElement.value = unit.suffix;
      newIngredientUnitTemplate.parentElement!.appendChild(unitOptionElement);
    });
  }

  private async getShoppingList(): Promise<void> {
    try {
      const startDateStr = this.startDate.toISOString().split("T")[0];
      const endDateStr = this.endDate.toISOString().split("T")[0];
      await this.shoppingListStore.getShoppingList(startDateStr, endDateStr, true);
    } catch (e: any) {
      this.message.timedMessage(`⚠ Unexpected error getting shopping list ⚠`);
      console.log(e);
      setError("plan");
    }
  }

  private initializeDateInputs(): void {
    // Set default start date to next Monday
    const today = new Date();
    const nextMonday = new Date();
    nextMonday.setDate(today.getDate() + ((1 + 7 - today.getDay()) % 7 || 7));

    // Set default end date to next Sunday (6 days after Monday)
    const nextSunday = new Date(nextMonday);
    nextSunday.setDate(nextMonday.getDate() + 6);

    this.startDate = nextMonday;
    this.endDate = nextSunday;
    this.currentMonth = new Date(nextMonday);

    // Update display
    this.updateDateRangeDisplay();
  }

  private async loadDateRange(): Promise<void> {
    if (this.startDate > this.endDate) {
      this.message.timedMessage(`⚠ Start date must be before end date ⚠`);
      return;
    }

    setLoading("shoppingList");
    await this.getShoppingList();
    this.populateList();
    setContent("shoppingList");
  }

  private setupCalendar(): void {
    const toggleButton = getElementById("toggleCalendarButton");
    const calendarContainer = getElementById("calendarContainer");
    const calendarDaysContainer = getElementById("calendarDays");
    const prevMonthBtn = getElementById("prevMonth");
    const nextMonthBtn = getElementById("nextMonth");
    const applyBtn = getElementById("applyDateRange");
    const clearBtn = getElementById("clearCalendar");

    // Toggle calendar visibility
    toggleButton.addEventListener("click", (e) => {
      e.stopPropagation();
      calendarContainer.classList.toggle("hidden");
      if (!calendarContainer.classList.contains("hidden")) {
        this.renderCalendar();
      }
    });

    // Use event delegation for date clicks
    calendarDaysContainer.addEventListener("click", (e) => {
      e.stopPropagation();
      const target = e.target as HTMLElement;
      const button = target.closest("button[data-date]");
      if (button) {
        const dateStr = button.getAttribute("data-date");
        if (dateStr) {
          const date = new Date(dateStr);
          this.handleDateClick(date);
        }
      }
    });

    // Use event delegation for date hover (mouseover bubbles, mouseenter doesn't)
    let lastHoveredDate: string | null = null;
    calendarDaysContainer.addEventListener("mouseover", (e) => {
      if (!this.isSelectingRange) return;
      const target = e.target as HTMLElement;
      const button = target.closest("button[data-date]");
      if (button) {
        const dateStr = button.getAttribute("data-date");
        if (dateStr && dateStr !== lastHoveredDate) {
          lastHoveredDate = dateStr;
          const date = new Date(dateStr);
          this.hoverDate = date;
          this.renderCalendar();
        }
      }
    });

    // Close calendar when clicking outside
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (!calendarContainer.contains(target) && !toggleButton.contains(target)) {
        calendarContainer.classList.add("hidden");
      }
    });

    // Month navigation
    prevMonthBtn.addEventListener("click", () => {
      this.currentMonth.setMonth(this.currentMonth.getMonth() - 1);
      this.renderCalendar();
    });

    nextMonthBtn.addEventListener("click", () => {
      this.currentMonth.setMonth(this.currentMonth.getMonth() + 1);
      this.renderCalendar();
    });

    // Apply date range
    applyBtn.addEventListener("click", async () => {
      calendarContainer.classList.add("hidden");
      await this.loadDateRange();
    });

    // Clear selection
    clearBtn.addEventListener("click", () => {
      this.isSelectingRange = false;
      this.hoverDate = null;
      this.renderCalendar();
    });
  }

  private renderCalendar(): void {
    const monthYear = getElementById("monthYear");
    const calendarDays = getElementById("calendarDays");

    // Update month/year display
    const monthNames = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"];
    monthYear.textContent = `${monthNames[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;

    // Clear existing days
    calendarDays.innerHTML = "";

    // Get first day of month and number of days
    const firstDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
    const lastDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0);
    const daysInMonth = lastDay.getDate();
    // Adjust for Monday-first week (0=Monday, 6=Sunday)
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      const emptyDay = document.createElement("div");
      emptyDay.className = "bg-gray-800 py-1.5";
      calendarDays.appendChild(emptyDay);
    }

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), day);
      const dayElement = document.createElement("button");
      dayElement.type = "button";

      // Time element for better semantic HTML
      const timeElement = document.createElement("time");
      timeElement.dateTime = date.toISOString().split("T")[0];
      timeElement.textContent = day.toString();
      dayElement.appendChild(timeElement);

      // Base classes
      let classes = "bg-gray-800 py-1.5 hover:bg-gray-700 focus:z-10";

      // Apply styling based on selection state
      const dateStr = this.formatDateKey(date);
      const startStr = this.formatDateKey(this.startDate);
      const endStr = this.formatDateKey(this.endDate);
      const isStart = dateStr === startStr;
      const isEnd = dateStr === endStr;
      const isInRange = date > this.startDate && date < this.endDate;

      // Check hover range preview
      let isInHoverRange = false;
      if (this.hoverDate && this.isSelectingRange) {
        const rangeStart = this.startDate < this.hoverDate ? this.startDate : this.hoverDate;
        const rangeEnd = this.startDate < this.hoverDate ? this.hoverDate : this.startDate;
        isInHoverRange = date > rangeStart && date < rangeEnd;
      }

      if (isStart || isEnd) {
        // Selected start or end date
        classes = "bg-indigo-600 font-semibold text-white hover:bg-indigo-500 focus:z-10";
      } else if (isInRange) {
        // Dates in selected range
        classes = "bg-indigo-600 bg-opacity-30 text-indigo-100 hover:bg-opacity-40 focus:z-10";
      } else if (isInHoverRange) {
        // Dates in hover preview range
        classes = "bg-indigo-600 bg-opacity-10 text-indigo-200 hover:bg-opacity-20 focus:z-10";
      }

      dayElement.className = classes;

      // Add data-date attribute for event delegation
      dayElement.setAttribute("data-date", date.toISOString());

      calendarDays.appendChild(dayElement);
    }

    // Fill remaining cells in the last week
    const totalCells = calendarDays.children.length;
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    for (let i = 0; i < remainingCells; i++) {
      const emptyDay = document.createElement("div");
      emptyDay.className = "bg-gray-800 py-1.5";
      calendarDays.appendChild(emptyDay);
    }
  }

  private handleDateClick(date: Date): void {
    if (!this.isSelectingRange) {
      // First click - set start date
      this.startDate = new Date(date);
      this.endDate = new Date(date);
      this.isSelectingRange = true;
      this.updateDateRangeDisplay();
    } else {
      // Second click - set end date
      if (date < this.startDate) {
        // If clicked date is before start, swap them
        this.endDate = new Date(this.startDate);
        this.startDate = new Date(date);
      } else {
        this.endDate = new Date(date);
      }
      this.isSelectingRange = false;
      this.hoverDate = null;
      this.updateDateRangeDisplay();
    }
    this.renderCalendar();
  }

  private updateDateRangeDisplay(): void {
    const display = getElementById("dateRangeDisplay");
    const startStr = this.formatDate(this.startDate);
    const endStr = this.formatDate(this.endDate);

    if (this.startDate.getTime() === this.endDate.getTime()) {
      display.textContent = startStr;
    } else {
      display.textContent = `${startStr} - ${endStr}`;
    }
  }

  private formatDate(date: Date): string {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  }

  private formatDateKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }

  private populateList(): void {

    const shopSectionTemplate = getElementDataForEach("shopSections");
    shopSectionTemplate.classList.remove("hidden");
    const shopSectionTemplateParent = shopSectionTemplate.parentElement!;
    shopSectionTemplateParent.innerHTML = "";
    shopSectionTemplateParent.appendChild(shopSectionTemplate);

    this.shoppingListStore.shopSections.forEach((shopSection) => {
      const newSection = shopSectionTemplate.cloneNode(true) as HTMLElement;
      newSection.querySelector("[data-selector=shopSection]")!.textContent = shopSection;

      const ingredientTemplate = newSection.querySelector("[data-for-each=ingredients]") as HTMLElement;
      ingredientTemplate.classList.remove("hidden");

      shopSectionTemplateParent.appendChild(newSection);
      const shopSectionIngredients = this.shoppingListStore.shoppingList.filter((item) => {
        return item.shop_section == shopSection;
      });

      shopSectionIngredients.forEach((item) => {
        const newIngredient = ingredientTemplate.cloneNode(true) as HTMLElement;
        newIngredient.querySelector("[data-selector=ingredientName]")!.textContent = item.ingredient_name;
        newIngredient.querySelector("[data-selector=ingredientAmount]")!.textContent = `${Math.round(item.quantity * 100) / 100} ${item.unit_suffix}`;
        newIngredient.querySelector("[data-selector=ingredientGotButton]")!.addEventListener("click", async () => {
          try {
            newIngredient.remove();
            this.inventoryStore.addToInventory(item);
            const index = this.shoppingListStore.shoppingList.indexOf(item);
            this.shoppingListStore.shoppingList.splice(index, 1);
          } catch (e: any) {
            this.message.timedMessage(`⚠ Unexpected error adding item to inventory ⚠`);
            console.log(e);
            throw new Error(e);
          }
        });
        newSection.appendChild(newIngredient);
      });
      ingredientTemplate.classList.add("hidden");
    });
    shopSectionTemplate.classList.add("hidden");
  }

  private async addExtraItem(): Promise<void> {
    const ingredientName = this.extraIngredientOptions.ingredient_name;
    const unitSuffix = this.extraIngredientOptions.unit_suffix;
    const quantity = this.extraIngredientOptions.quantity;

    if (ingredientName && unitSuffix && quantity) {
      try {
        await this.shoppingListStore.addExtraShoppingListItem(ingredientName, quantity, unitSuffix);
        this.message.timedMessage(`Added ${quantity} ${unitSuffix} of ${ingredientName} to shopping list`);
        setLoading("shoppingList");
        await this.getShoppingList();
        this.populateList();
        setContent("shoppingList");
      } catch (e: any) {
        this.message.timedMessage(`⚠ Unexpected error adding item to shopping list ⚠`);
        console.log(e);
        throw new Error(e);
      }
    } else {
      this.message.timedMessage(`⚠ Please fill in all fields ⚠`);
    }
  }

  private async clearAllItems(): Promise<void> {
    try {
      await this.shoppingListStore.clearAllShoppingListItems();
      this.message.timedMessage(`All shopping list items cleared`);
      setLoading("shoppingList");
      await this.getShoppingList();
      this.populateList();
      setContent("shoppingList");
    } catch (e: any) {
      this.message.timedMessage(`⚠ Unexpected error clearing shopping list ⚠`);
      console.log(e);
      throw new Error(e);
    }
  }
}
